import { useEffect, useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import capitalize from "lodash/capitalize";

import { Box, Flex } from "@/components/layout/flexbox";
import { roleplayRankStyle } from "@/components/pages_logged_in/program/components/program-details/const";
import { ScenarioParticipant } from "@/components/pages_logged_in/program/components/program-details/ScenarioParticipantTable";
import { sortScenariosByDifficulty } from "@/components/pages_logged_in/roleplays/utils";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Avatar } from "@/components/pieces/users/Avatar";
import {
  CARD_CLASSES,
  CTA_COLOR,
  MIDTEXT_COLOR,
  SUBTEXT_COLOR,
} from "@/css/constants";
import {
  RoleplayRank,
  useProgramRoleplayScenariosAnalyticsQueryLazyQuery,
} from "@/graphql";
import { AnalyticsRoleplaySession, AnalyticsScenario } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

const rankLevel: Record<RoleplayRank, number> = {
  [RoleplayRank.Gold]: 4,
  [RoleplayRank.Silver]: 3,
  [RoleplayRank.Bronze]: 2,
  [RoleplayRank.Unranked]: 1,
};

interface StatisticFromSessions {
  totalResponsesByUser: number;
  rankStats: Record<RoleplayRank, number>;
}

function getStatisticFromSessions(
  roleplaySessions: AnalyticsRoleplaySession[],
): StatisticFromSessions {
  const bestRankByUser = roleplaySessions.reduce(
    (acc, { userId, rank }) => {
      const nonNullUserId = userId ?? "";
      const previousRank = acc[nonNullUserId];
      const sessionRank = rank ?? RoleplayRank.Unranked;

      // If the session don't have rank, ignore it
      if (!previousRank || rankLevel[sessionRank] > rankLevel[previousRank]) {
        acc[nonNullUserId] = sessionRank;
      }
      return acc;
    },
    {} as Record<string, RoleplayRank>,
  );

  const rankCounts = Object.values(bestRankByUser).reduce<
    Record<RoleplayRank, number>
  >(
    (acc, rank) => {
      acc[rank]++;
      return acc;
    },
    {
      [RoleplayRank.Gold]: 0,
      [RoleplayRank.Silver]: 0,
      [RoleplayRank.Bronze]: 0,
      [RoleplayRank.Unranked]: 0,
    },
  );

  return {
    totalResponsesByUser: Object.keys(bestRankByUser).length,
    rankStats: rankCounts,
  };
}

interface ScenarioProps {
  scenario: AnalyticsScenario;
  componentId: string;
}

function Scenario({ scenario, componentId }: ScenarioProps) {
  const { name, shortDesc, totalParticipants } = scenario;
  const [isScenarioParticipantVisible, setIsScenarioParticipantVisible] =
    useState(false);

  const sessions = ensureNonNullArray(scenario.roleplaySessions);

  const { totalResponsesByUser, rankStats } =
    getStatisticFromSessions(sessions);

  const statisticData = Object.entries(rankStats).map(([key, value]) => {
    return {
      label: capitalize(key),
      count: value,
      style: roleplayRankStyle[key as RoleplayRank],
    };
  });

  const notStartNum = totalParticipants
    ? totalParticipants - totalResponsesByUser
    : 0;

  statisticData.push({
    label: capitalize("not started"),
    count: notStartNum,
    style: roleplayRankStyle[RoleplayRank.Unranked],
  });

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      mt={24}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
    >
      <Flex>
        <Avatar
          user={{
            name: scenario.personaName,
            photo: scenario.personaPhotoUrl,
          }}
          size={64}
        />
        <Flex flexDirection="column" ml={3} justifyContent="center">
          <Box fontSize={18} fontWeight={600}>
            {name}
          </Box>
          <Box fontSize={16} color={MIDTEXT_COLOR}>
            {shortDesc}
          </Box>
        </Flex>
      </Flex>
      <Box my={16} flex={1} height={12} width={1} display={["none", "flex"]}>
        {statisticData
          .filter((item) => item?.count > 0)
          .map((item, index) => (
            <Flex
              key={index}
              flex={item?.count}
              justifyContent={"center"}
              css={
                index !== statisticData.length - 1 ? { marginRight: 5 } : null
              }
            >
              <Box
                width={1}
                backgroundColor={item?.style?.color}
                height={12}
                css={{
                  border: `solid 1px ${item?.style?.borderColor}`,
                  borderRadius: 4,
                  borderStyle: item?.style?.borderStyle,
                }}
              ></Box>
            </Flex>
          ))}
      </Box>
      <Flex flex={1} flexDirection={["column", "row"]}>
        {statisticData.map((item) => (
          <Box key={item.label} mr={"24px"} justifyContent={"center"}>
            <Box color={SUBTEXT_COLOR}>{item?.label}</Box>
            <Flex alignItems={"center"}>
              <RankIcon rank={item?.style?.rankIcon} size={24} />
              <Box fontSize={18} fontWeight={600} ml={"5px"}>
                {item.count}
              </Box>
            </Flex>
          </Box>
        ))}
      </Flex>
      <Clickable
        onClick={() =>
          setIsScenarioParticipantVisible(!isScenarioParticipantVisible)
        }
      >
        <Flex mt={36} color={CTA_COLOR} alignItems="center">
          See Performance Per Participant
          <Box ml={2}>
            <Icon
              icon={
                isScenarioParticipantVisible ? "chevron-up" : "chevron-down"
              }
            />
          </Box>
        </Flex>
      </Clickable>

      {isScenarioParticipantVisible && (
        <ScenarioParticipant
          scenarioId={scenario.id}
          componentId={componentId}
        />
      )}
    </Box>
  );
}

interface PerformancePerScenarioProps {
  componentId: string;
}

export function PerformancePerScenario({
  componentId,
}: PerformancePerScenarioProps) {
  const [fetch, { data }] =
    useProgramRoleplayScenariosAnalyticsQueryLazyQuery();

  const statisticData = ensureNonNullArray(
    data?.programRoleplayScenariosAnalytics,
  );

  useEffect(() => {
    fetch({ variables: { programComponentId: componentId } });
  }, []);

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
    >
      <Box fontSize={18} fontWeight={600}>
        Performance Per Scenario
      </Box>
      {sortScenariosByDifficulty(statisticData).map((scenario) => (
        <Scenario
          key={scenario.id}
          scenario={scenario}
          componentId={componentId}
        />
      ))}
    </Box>
  );
}
